'use client'
import { useCallback, useMemo } from 'react'
import { useRouter as usePagesRouter } from 'next/compat/router'
import { useRouter as useAppRouter } from 'next/navigation'
import { getIsPciRoutingRequired } from '@/features/shared/utils/getIsPciRoutingRequired'
import NProgress from 'nprogress'
import { isSamePathAndQuery } from '@/features/shared/utils/url'
import { getIsCrossRouterNavigation } from '@/features/shared/utils/appRouterNavigation'

type NavigateOptions = {
  // scroll to the top after navigating. left unset, next's own default applies.
  // the app router's history API never scrolls, so shallow updates there only
  // scroll when this is explicitly true.
  scroll?: boolean
  // update the URL without re running server data fetching.
  // pages router -> shallow routing. router.query/asPath sync, router.events fire.
  // app router -> history API. usePathname/useSearchParams sync.
  // same page URL changes only (e.g. query params).
  shallow?: boolean
  // app router ONLY. forwarded to React.addTransitionType for the navigation transition.
  transitionTypes?: string[]
}

export type SegwayRouter = {
  back: () => void
  prefetch: (href: string) => void
  push: (href: string, options?: NavigateOptions) => Promise<unknown>
  replace: (href: string, options?: NavigateOptions) => Promise<unknown>
}

export const useSegwayRouter = (): SegwayRouter => {
  const appRouter = useAppRouter()
  const pagesRouter = usePagesRouter()

  // if the page is using the pages router, we need to check if the href is a cross router navigation
  const isCrossRouter = useCallback(
    (href: string) => {
      return getIsCrossRouterNavigation({ href, isPages: Boolean(pagesRouter) })
    },
    [pagesRouter],
  )

  const handleRouting = useCallback(
    async (
      href: string,
      options: NavigateOptions | undefined,
      method: 'push' | 'replace',
    ): Promise<boolean | void> => {
      if (isSamePathAndQuery(href)) return

      if (isCrossRouter(href) || getIsPciRoutingRequired({ href })) {
        // assigning href always pushes, so honor the caller's history intent
        if (method === 'replace') window.location.replace(href)
        else window.location.href = href
        return
      }

      NProgress.start()

      const { scroll, shallow, transitionTypes } = options ?? {}

      if (shallow) {
        if (pagesRouter) {
          // only forward scroll when the caller set it, so next's default applies.
          return pagesRouter[method](href, undefined, {
            shallow: true,
            scroll,
          })
        }
        // app router requires null as the first arg to pick up the change.
        window.history[method === 'push' ? 'pushState' : 'replaceState'](null, '', href)
        return
      }

      if (pagesRouter) {
        // omit the options object when there is nothing to set, so we inherit
        // next's defaults rather than passing an explicit undefined.
        return scroll === undefined
          ? pagesRouter[method](href)
          : pagesRouter[method](href, undefined, { scroll })
      }
      return appRouter[method](href, { scroll, transitionTypes })
    },
    [pagesRouter, appRouter, isCrossRouter],
  )

  const push = useCallback<SegwayRouter['push']>(
    (href, options) => handleRouting(href, options, 'push'),
    [handleRouting],
  )

  const replace = useCallback<SegwayRouter['replace']>(
    (href, options) => handleRouting(href, options, 'replace'),
    [handleRouting],
  )

  const prefetch = useCallback<SegwayRouter['prefetch']>(
    (href) => {
      // the other router's payload is unusable here, so fetching it is pure waste
      if (isCrossRouter(href)) return

      return (pagesRouter ?? appRouter).prefetch(href)
    },
    [pagesRouter, appRouter, isCrossRouter],
  )

  const back = useCallback(() => (pagesRouter ?? appRouter).back(), [pagesRouter, appRouter])

  return useMemo(() => ({ back, prefetch, push, replace }), [back, prefetch, push, replace])
}
